// Bakes markup into a PDF with pdf-lib: strokes and shapes as vector graphics
// on the original pages (content stays intact and crisp at any zoom), text
// stamps as rasterized transparent PNGs — script-agnostic, so Japanese text
// needs no font embedding.

import {
  concatTransformationMatrix, EncryptedPDFError, LineCapStyle, PDFDocument,
  popGraphicsState, pushGraphicsState, rgb, type PDFPage, type RGB,
} from 'pdf-lib';
import {
  arrowheadPoints, cloudArcs, cloudBumpPx, cloudSvgPath, drawItems, normalizeRect,
  TEXT_LINE_HEIGHT, textFontString, wrapTextLines,
} from '../markupRender';
import { MARKER_ALPHA, resolveMarkupColor, type IMarkupPoint, type ItemsByPage, type MarkupItem } from '../markupTypes';

/** The bake couldn't run because the source PDF is encrypted. pdf.js decrypts
 *  owner-password files so the editor opens and draws fine; pdf-lib has no
 *  decryption, and forcing it through would emit garbled markup. */
export class EncryptedSourcePdfError extends Error {
  constructor() {
    super('the source pdf is encrypted');
    this.name = 'EncryptedSourcePdfError';
  }
}

/** Why a bake failed, at the granularity the editor can explain to the user. */
export type ExportFailure = 'generic' | 'encrypted';

export function exportFailureOf(err: unknown): ExportFailure {
  return err instanceof EncryptedSourcePdfError ? 'encrypted' : 'generic';
}

/** A page's /Rotate as one of the four quarter turns. */
function pageRotation(page: PDFPage): 0 | 90 | 180 | 270 {
  const deg = ((Math.round(page.getRotation().angle / 90) * 90) % 360 + 360) % 360;
  return deg as 0 | 90 | 180 | 270;
}

/**
 * The page as the viewer saw it: CropBox dimensions with /Rotate applied — the
 * space pdf.js rendered, and therefore the space the markup percentages mean.
 */
export function viewportSize(page: PDFPage): { width: number; height: number } {
  const box = page.getCropBox();
  return pageRotation(page) % 180 === 0
    ? { width: box.width, height: box.height }
    : { width: box.height, height: box.width };
}

/**
 * Viewport space (bottom-left of the page AS DISPLAYED) → PDF user space, as a
 * CTM. pdf-lib draws in raw user space, which ignores /Rotate and starts at the
 * MediaBox origin; without this the markup silently lands rotated and offset.
 */
export function viewportToUserSpace(page: PDFPage): [number, number, number, number, number, number] {
  const { x, y, width, height } = page.getCropBox();
  switch (pageRotation(page)) {
    case 90: return [0, 1, -1, 0, x + width, y];
    case 180: return [-1, 0, 0, -1, x + width, y + height];
    case 270: return [0, -1, 1, 0, x, y + height];
    default: return [1, 0, 0, 1, x, y];
  }
}

/** Percentage point → PDF coordinates (origin bottom-left, y up). */
export function pctToPdfPoint(p: IMarkupPoint, pageWidth: number, pageHeight: number): { x: number; y: number } {
  return { x: (p.x / 100) * pageWidth, y: pageHeight - (p.y / 100) * pageHeight };
}

/** A CSS color value ("#rgb", "#rrggbb" or "rgb(r, g, b)") as a pdf-lib RGB.
 *  The short form is not hypothetical — tokens.css writes `--white: #fff`, and
 *  a custom property's computed value is the literal token, unnormalised. */
export function cssColorToRgb(value: string): RGB {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (hex) {
    const digits = hex[1]!;
    const full = digits.length === 3 ? digits.replace(/./g, (d) => d + d) : digits;
    const n = parseInt(full, 16);
    return rgb(((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255);
  }
  const fn = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(value.trim());
  if (fn) return rgb(Number(fn[1]) / 255, Number(fn[2]) / 255, Number(fn[3]) / 255);
  return rgb(0, 0, 0);
}

/** A freehand polyline as an SVG path string (y-down page space). */
function polylineSvgPath(points: IMarkupPoint[], pageW: number, pageH: number): string {
  const fmt = (n: number) => Number(n.toFixed(2));
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${fmt((p.x / 100) * pageW)} ${fmt((p.y / 100) * pageH)}`)
    .join(' ');
}

/**
 * A text BOX rendered onto a transparent canvas at 2× for embedding: wrapped
 * lines, alignment and bidi direction inside the box — via the same renderer
 * the live overlay uses, so the baked PDF matches what the editor showed.
 */
function rasterizeTextBox(
  item: Extract<MarkupItem, { kind: 'text' }>,
  boxWidthPt: number,
  fontPt: number,
  cssColor: string,
): { dataUrl: string; height: number } {
  const scale = 2;
  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) throw new Error('canvas 2d context unavailable');
  probe.font = textFontString(fontPt * scale, item);
  probe.direction = item.direction ?? 'ltr';
  const lines = wrapTextLines((s) => probe.measureText(s).width, item.text, boxWidthPt * scale);
  const heightPx = Math.max(1, Math.ceil(Math.max(1, lines.length) * fontPt * scale * TEXT_LINE_HEIGHT));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(boxWidthPt * scale));
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d')!;
  // The box occupies the whole canvas — reuse the shared renderer 1:1.
  drawItems(
    ctx,
    [{ ...item, x: 0, y: 0, w: 100, size: (fontPt * scale) / canvas.width }],
    canvas.width,
    canvas.height,
    () => cssColor,
  );
  return { dataUrl: canvas.toDataURL('image/png'), height: heightPx / scale };
}

/** Draws one item in VIEWPORT space — the caller has already concatenated the
 *  matrix that maps it onto the page. */
async function drawItem(
  pdf: PDFDocument,
  page: PDFPage,
  item: MarkupItem,
  view: { width: number; height: number },
): Promise<void> {
  const { width: pageW, height: pageH } = view;

  const cssColor = resolveMarkupColor(item.color);

  if (item.kind === 'text') {
    const fontPt = Math.max(8, item.size * pageW);
    const boxW = Math.max(1, (item.w / 100) * pageW);
    const raster = rasterizeTextBox(item, boxW, fontPt, cssColor);
    const image = await pdf.embedPng(raster.dataUrl);
    const at = pctToPdfPoint({ x: item.x, y: item.y }, pageW, pageH);
    // (x, y) is the box's TOP-left — shift down by the raster's height.
    page.drawImage(image, { x: at.x, y: at.y - raster.height, width: boxW, height: raster.height });
    return;
  }

  const color = cssColorToRgb(cssColor);
  const thickness = Math.max(0.5, item.width * pageW);

  if (item.tool === 'cloud') {
    // Arcs are computed in y-down page space; drawSvgPath interprets SVG paths
    // y-down from its origin, so anchoring at the page's top-left lines up.
    const arcs = cloudArcs(item.points[0]!, item.points[1]!, pageW, pageH, cloudBumpPx(thickness, pageW));
    page.drawSvgPath(cloudSvgPath(arcs), {
      x: 0,
      y: pageH,
      borderColor: color,
      borderWidth: thickness,
    });
    return;
  }

  if (item.tool === 'rect') {
    const r = normalizeRect(item.points[0]!, item.points[1]!);
    page.drawRectangle({
      x: (r.x / 100) * pageW,
      y: pageH - ((r.y + r.h) / 100) * pageH,
      width: (r.w / 100) * pageW,
      height: (r.h / 100) * pageH,
      borderColor: color,
      borderWidth: thickness,
    });
    return;
  }

  if (item.tool === 'ellipse') {
    // Inscribed in the dragged rectangle — centre + radii, y-flipped.
    const r = normalizeRect(item.points[0]!, item.points[1]!);
    page.drawEllipse({
      x: ((r.x + r.w / 2) / 100) * pageW,
      y: pageH - ((r.y + r.h / 2) / 100) * pageH,
      xScale: (r.w / 200) * pageW,
      yScale: (r.h / 200) * pageH,
      borderColor: color,
      borderWidth: thickness,
    });
    return;
  }

  if (item.tool === 'marker') {
    // ONE translucent path for the whole stroke — per-segment lines would
    // double-darken at every joint where the round caps overlap.
    page.drawSvgPath(polylineSvgPath(item.points, pageW, pageH), {
      x: 0,
      y: pageH,
      borderColor: color,
      borderWidth: thickness,
      borderOpacity: MARKER_ALPHA,
      borderLineCap: LineCapStyle.Round,
    });
    return;
  }

  const line = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    page.drawLine({ start: a, end: b, color, thickness, lineCap: LineCapStyle.Round });

  for (let i = 1; i < item.points.length; i += 1) {
    line(
      pctToPdfPoint(item.points[i - 1]!, pageW, pageH),
      pctToPdfPoint(item.points[i]!, pageW, pageH),
    );
  }

  if (item.tool === 'arrow' && item.points.length >= 2) {
    const from = item.points[0]!;
    const to = item.points[item.points.length - 1]!;
    // The wing geometry is computed in canvas space (y down) and flipped here.
    const wings = arrowheadPoints(from, to, pageW, pageH, thickness * 4);
    const head = pctToPdfPoint(to, pageW, pageH);
    for (const wing of wings) line({ x: wing.x, y: pageH - wing.y }, head);
  }
}

export async function exportPdfWithMarkup(
  original: Blob,
  itemsByPage: ItemsByPage,
  fileName: string,
): Promise<File> {
  let pdf: PDFDocument;
  try {
    pdf = await PDFDocument.load(await original.arrayBuffer());
  } catch (err) {
    if (err instanceof EncryptedPDFError) throw new EncryptedSourcePdfError();
    throw err;
  }
  const pages = pdf.getPages();

  for (const [pageNo, items] of Object.entries(itemsByPage)) {
    const page = pages[Number(pageNo) - 1];
    if (!page || items.length === 0) continue;
    // One q/cm … Q around the whole page's markup: everything below is drawn
    // in viewport space, and the matrix puts it where the user saw it.
    page.pushOperators(pushGraphicsState(), concatTransformationMatrix(...viewportToUserSpace(page)));
    const view = viewportSize(page);
    for (const item of items) await drawItem(pdf, page, item, view);
    page.pushOperators(popGraphicsState());
  }

  const bytes = await pdf.save();
  return new File([bytes as BlobPart], fileName, { type: 'application/pdf' });
}
